// useSearchFormik.js
import * as Yup from "yup";
import { useFormik } from "formik";
import { SearchApplicationAPI, SearchAdvancePaymentApplication, SearchIndividualApplication, AllComplaintAPI } from "../network/APIs/APIs";
import { composeSearchApplicationId, getSearchApplicationIdPrefix, getSearchApplicationIdRemainingLength } from "../components/SearchPopups/SearchPopup";
import { getApplicationProgressStatus } from "../utils/applicationProgressStatus";
import { cfpBaseUrl } from "../network/urls";

const isWoiSearchRow = (row = {}) => {
  const typeName = String(row.applicationTypeName || row.complaintType || "").toLowerCase();
  const appId = String(row.applicationId || row.eOpsTrackingId || "").toUpperCase();
  const typeId = Number(row.applicationTypeId);
  return (
    typeName.includes("withdrawal of installment") ||
    appId.startsWith("IWD-") ||
    typeId === 19
  );
};

const enrichWoiSearchRow = (row = {}) => {
  if (!isWoiSearchRow(row)) return row;

  const progressId = row.applicationProgressId ?? row.progressId;
  const statusLabel = getApplicationProgressStatus(
    19,
    progressId,
    row.objectionStatus,
    row.status,
    "Withdrawal of Installment",
  );

  return {
    ...row,
    applicationTypeId: row.applicationTypeId || 19,
    applicationTypeName:
      row.applicationTypeName ||
      row.complaintType ||
      "Withdrawal of Installment",
    applicationProgressId: progressId ?? row.applicationProgressId,
    name: row.applicantName || row.consumerName || row.name,
    applicantName: row.applicantName || row.consumerName || row.name,
    applicantCNIC: row.applicantCNIC || row.consumerCNIC || row.cnic,
    cnic: row.cnic || row.applicantCNIC || row.consumerCNIC,
    status: statusLabel || row.status || "-",
  };
};

const isMrcSearchRow = (row = {}) => {
  const typeName = String(row.applicationTypeName || row.complaintType || "").toLowerCase();
  const appId = String(row.applicationId || row.eOpsTrackingId || "");
  const typeId = Number(row.applicationTypeId);
  return (
    typeName.includes("meter reading correction") ||
    appId.toUpperCase().startsWith("MRC-") ||
    typeId === 11
  );
};

const resolveMrcProgressId = (row = {}) => {
  const candidates = [
    row.applicationProgressId,
    row.ApplicationProgressId,
    row.progressId,
    row.mRCProgressId,
    row.mrcProgressId,
  ];
  for (const value of candidates) {
    if (value === 0 || value === "0") return 0;
    if (value == null || value === "" || value === "-") continue;
    const num = Number(value);
    if (!Number.isNaN(num)) return num;
  }
  return null;
};

const enrichMrcSearchRow = async (row, token) => {
  if (!isMrcSearchRow(row)) return row;

  let detail = { ...row };
  const appId = detail.applicationId || detail.eOpsTrackingId;
  const hasStepName = Boolean(
    detail.mRCProgStatusName || detail.mrcProgStatusName,
  );
  let progressId = resolveMrcProgressId(detail);

  if (appId && (!hasStepName && progressId == null)) {
    const fetched = await SearchIndividualApplication(token, appId, "11");
    if (fetched) {
      detail = { ...detail, ...fetched };
      progressId = resolveMrcProgressId(detail);
    }
  }

  // Fallback: basic MRC history currentStatus has mRCProgStatusName
  if (
    appId &&
    !detail.mRCProgStatusName &&
    !detail.mrcProgStatusName &&
    progressId == null
  ) {
    try {
      const myHeaders = new Headers();
      myHeaders.append("Authorization", token);
      myHeaders.append("Content-Type", "application/json");
      const response = await fetch(
        `${cfpBaseUrl}mrc/getBasicMRCHistory`,
        {
          method: "POST",
          headers: myHeaders,
          body: JSON.stringify({ applicationId: appId }),
        },
      );
      const result = await response.json();
      const current = result?.data?.currentStatus?.[0];
      if (current) {
        detail = {
          ...detail,
          ...current,
          applicationProgressId:
            current.applicationProgressId ?? detail.applicationProgressId,
          mRCProgStatusName: current.mRCProgStatusName,
        };
        progressId = resolveMrcProgressId(detail);
      }
    } catch (e) {
      // ignore
    }
  }

  const resolvedProgressId = resolveMrcProgressId(detail);
  const apiStep =
    detail.mRCProgStatusName ||
    detail.mrcProgStatusName ||
    "";
  const mappedStep = getApplicationProgressStatus(
    11,
    resolvedProgressId,
    detail.objectionStatus,
    "",
    "Meter Reading Correction",
  );
  const statusLabel = apiStep || mappedStep || detail.status || "-";

  return {
    ...detail,
    applicationTypeId: detail.applicationTypeId || 11,
    applicationTypeName:
      detail.applicationTypeName ||
      detail.complaintType ||
      "Meter Reading Correction",
    applicationProgressId:
      resolvedProgressId ?? detail.applicationProgressId,
    name: detail.applicantName || detail.consumerName || detail.name,
    applicantName:
      detail.applicantName || detail.consumerName || detail.name,
    mRCProgStatusName: apiStep || mappedStep || detail.mRCProgStatusName,
    status: statusLabel,
  };
};

/**
 * Custom hook for managing search form using Formik and Yup validation.
 * @component
 * @memberof Hooks
 * @param {Object} params - Parameters for the search form.
 * @param {number} params.selectedTab - Index of the selected search tab.
 * @param {Function} params.setSelectedValueOfTab - Function to set the selected search value.
 * @param {Function} params.setResultSearchedData - Function to set the result data from Search API.
 * @param {Function} params.setResultSearchedData1 - Function to set the result data from All Complaint API.
 * @param {string} params.token - Authorization token for API requests.
 * @param {Function} params.handleDialogOfApplicationOpen - Function to handle opening the search dialog.
 * @param {Function} params.handleCloseSearchBarPopup - Function to close the search bar popup.
 * @param {Object} params.initialValues - Initial values for the form fields.
 *
 * @returns {Object} Formik instance for managing the search form.
 */
// export const useSearchFormik = ({ selectedTab, setSelectedValueOfTab, setResultSearchedData, setResultSearchedData1, token, handleDialogOfApplicationOpen, handleCloseSearchBarPopup, initialValues }) => {
//     return useFormik({
//         initialValues: initialValues,
//         validationSchema: Yup.object({
//             iv_reference_no: Yup.string().when('selectedTab', {
//                 is: () => selectedTab == "0",
//                 then: Yup.string().required('Enter 14 digit Reference No.').min(14, 'Reference No. should be 14 digits.').max(14, 'Reference No. should be 14 digits.'),
//                 otherwise: Yup.string().optional()
//             }),
//             iv_application_id: Yup.string().when('selectedTab', {
//                 is: () => selectedTab == "1",
//                 then: Yup.string().required('Enter Application ID.').min(17, 'Application ID. should be minimum 17 digits.').max(19, 'Application ID. should be maximum 19 digits.'),
//                 otherwise: Yup.string().optional()
//             }),
//             iv_cnic: Yup.string().when('selectedTab', {
//                 is: () => selectedTab == "2",
//                 then: Yup.string().required('Enter 13 digit CNIC.').min(13, 'CNIC should be 13 digits.').max(13, 'CNIC should be 13 digits.'),
//                 otherwise: Yup.string().optional()
//             }),
//             iv_applicant_name: Yup.string().when('selectedTab', {
//                 is: () => selectedTab == "3",
//                 then: Yup.string().required('Enter Applicant Name.').min(3, `Name can't be less than 3 alphabets.`),
//                 otherwise: Yup.string().optional()
//             }),
//         }),
//         onSubmit: async (values, { resetForm }) => {
//             let searchWord = '';

//             if (selectedTab === 0) {
//                 searchWord = values.iv_reference_no;
//                 setSelectedValueOfTab(searchWord);
//             } else if (selectedTab === 1) {
//                 searchWord = values.iv_application_id;
//                 setSelectedValueOfTab(searchWord);
//             } else if (selectedTab === 2) {
//                 searchWord = values.iv_cnic;
//                 setSelectedValueOfTab(searchWord);
//             } else if (selectedTab === 3) {
//                 searchWord = values.iv_applicant_name;
//                 setSelectedValueOfTab(searchWord);
//             }

//             const result = await AllComplaintAPI(token, searchWord);
//             if (result) {
//                 setResultSearchedData1(result);
//                 handleDialogOfApplicationOpen();
//                 handleCloseSearchBarPopup();
//                 resetForm();
//             }
//             const results = await SearchAPI(token, searchWord);
//             if (results) {
//                 setResultSearchedData(results);
//                 handleDialogOfApplicationOpen();
//                 handleCloseSearchBarPopup();
//                 resetForm();
//             }
//         },
//     });
// };


export const useSearchFormik = ({
    selectedTab,
    setSelectedValueOfTab,
    setResultSearchedData,
    setResultSearchedData1,
    token,
    handleDialogOfApplicationOpen,
    handleCloseSearchBarPopup,
    initialValues,
    setViewBackdrop,
    companyCode,
    isAdmin = false,
    officeCode = '',
    hierarchy,
  }) => {
    return useFormik({
      initialValues,
      validationSchema: Yup.object({
        iv_reference_no: Yup.string().when('selectedTab', {
          is: () => selectedTab == "0",
          then: Yup.string()
            .required('Please enter a 14-digit reference number.')
            .min(14, 'Reference number must be 14 digits.')
            .max(14, 'Reference number must be 14 digits.'),
          otherwise: Yup.string().optional(),
        }),
        iv_application_id: Yup.string().when('selectedTab', {
          is: () => selectedTab == "1",
          then: Yup.string()
            .required('Please enter an application ID.')
            .test(
              'remaining-application-id-length',
              function validateRemainingApplicationId(value) {
                const officeContext = {
                  isAdmin,
                  officeCode,
                  hierarchy,
                  selectedCompanyCode: this.parent.iv_company_code,
                };
                const prefix = getSearchApplicationIdPrefix(
                  this.parent.iv_application_type,
                  officeContext,
                );
                const remainingLength = getSearchApplicationIdRemainingLength(
                  officeContext,
                  this.parent.iv_application_type,
                );
                // Field already stores the suffix after the visible prefix.
                // Do not re-strip office digits — remaining IDs often start
                // with the same office code (e.g. NC-13 + 131260909004).
                const suffixDigits = String(value || '').replace(/[^0-9]/g, '');

                if (suffixDigits.length === remainingLength) {
                  return true;
                }

                return this.createError({
                  message: `Enter the remaining ${remainingLength}-digit ID after ${prefix || 'the prefix'}.`,
                });
              },
            ),
          otherwise: Yup.string().optional(),
        }),
        iv_cnic: Yup.string().when('selectedTab', {
          is: () => selectedTab == "2",
          then: Yup.string()
            .required('Please enter a 13-digit CNIC.')
            .min(13, 'CNIC must be at least 13 digits.')
            .max(13, 'CNIC must be 13 digits.'),
          otherwise: Yup.string().optional(),
        }),
        iv_applicant_name: Yup.string().when('selectedTab', {
          is: () => selectedTab == "3",
          then: Yup.string()
            .required('Please enter the applicant name.')
            .min(3, 'Name must be at least 3 characters.'),
          otherwise: Yup.string().optional(),
        }),
        iv_application_type: Yup.string().required('Please select a service.'),
        iv_company_code: isAdmin
          ? Yup.string().required('Please select a company.')
          : Yup.string().optional(),
      }),
      onSubmit: async (values, { resetForm }) => {
        if (!String(values.iv_application_type || '').trim()) {
          return;
        }
        if (isAdmin && !String(values.iv_company_code || '').trim()) {
          return;
        }
        setViewBackdrop(true);
        let searchWord = '';
  
        if (selectedTab === 0) {
          searchWord = values.iv_reference_no;
        } else if (selectedTab === 1) {
          searchWord = composeSearchApplicationId(
            values.iv_application_type,
            values.iv_application_id,
            {
              isAdmin,
              officeCode,
              hierarchy,
              selectedCompanyCode: values.iv_company_code,
            },
          );
        } else if (selectedTab === 2) {
          searchWord = values.iv_cnic;
        } else if (selectedTab === 3) {
          searchWord = values.iv_applicant_name;
        }
  
        setSelectedValueOfTab(searchWord);
  
        try {
          let results = await SearchApplicationAPI({
            token,
            searchById: Number(selectedTab) + 1,
            searchByValue: searchWord,
            applicationType: values.iv_application_type,
            companyCode: String(
              values.iv_company_code || companyCode || ""
            ).slice(0, 2),
          });

          if (
            (!results || results.length === 0) &&
            /^AP-/i.test(searchWord)
          ) {
            const advanceResult = await SearchAdvancePaymentApplication(
              token,
              searchWord
            );
            if (advanceResult) {
              results = [
                {
                  ...advanceResult,
                  applicationTypeName:
                    advanceResult.applicationTypeName || "Advance Payment",
                  applicationTypeId: advanceResult.applicationTypeId || 18,
                  name: advanceResult.applicantName,
                },
              ];
            }
          }

          if (
            (!results || results.length === 0) &&
            /^MRC-/i.test(searchWord)
          ) {
            const mrcResult = await SearchIndividualApplication(
              token,
              searchWord,
              "11"
            );
            if (mrcResult) {
              results = [mrcResult];
            }
          }

          if (
            (!results || results.length === 0) &&
            (Number(values.iv_application_type) === 19 || /^IWD-/i.test(searchWord)) &&
            selectedTab === 1
          ) {
            const woiResult = await SearchIndividualApplication(
              token,
              searchWord,
              "19"
            );
            if (woiResult) {
              results = [woiResult];
            }
          }

          if (results && results.length > 0) {
            results = await Promise.all(
              results.map(async (row) => {
                const mrcRow = await enrichMrcSearchRow(row, token);
                return enrichWoiSearchRow(mrcRow);
              }),
            );
            setResultSearchedData(results);
          } else {
            setResultSearchedData([]);
          }
  
          handleDialogOfApplicationOpen();
          handleCloseSearchBarPopup();
          resetForm({
            values: {
              ...initialValues,
              iv_application_type: "",
              iv_company_code: isAdmin
                ? String(values.iv_company_code || initialValues.iv_company_code || "")
                : "",
            },
          });
        } catch (error) {
          // Backdrop is always cleared in finally.
        } finally {
          setViewBackdrop(false);
        }
      },
    });
  };
  